import { useEffect, useState } from "react";
import { Share, X } from "lucide-react";

const STORAGE_KEY = "chalkos.a2hs_dismissed_v1";
const SHOW_DELAY_MS = 4500;

/**
 * One-time "Add to Home Screen" prompt for iOS Safari visitors.
 *
 * iOS has no native install prompt (no `beforeinstallprompt` event), so users
 * never discover that they can pin Chalk OS to their home screen. Without that,
 * the cookie + standalone-mode work in iteration 18 are invisible. This toast
 * appears once after the app shell mounts, points users at the share menu,
 * and never shows again after a single dismissal.
 *
 * Gating:
 *  • iOS only — `/iPhone|iPod|iPad/`. Skipped on Android, desktop, and
 *    Chrome/Firefox/Edge on iOS (those would never show the Safari share menu).
 *  • Not already in standalone — checks both `display-mode: standalone` and
 *    the legacy `navigator.standalone`, so anyone who already installed the
 *    PWA is silent.
 *  • Once-per-device — sets `chalkos.a2hs_dismissed_v1` in localStorage on
 *    dismiss. Bumping the version key (e.g. v2) re-prompts everyone after a
 *    redesign.
 */
export default function AddToHomeScreenPrompt() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!shouldShow()) return undefined;
    const timer = setTimeout(() => setShow(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  function dismiss() {
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch { /* ignore */ }
    setShow(false);
  }

  if (!show) return null;

  return (
    <div
      data-testid="a2hs-prompt"
      role="dialog"
      aria-label="Add Chalk OS to your home screen"
      style={{
        position: "fixed",
        // Float just above the bottom nav + iOS home indicator.
        left: 12, right: 12,
        bottom: "calc(80px + env(safe-area-inset-bottom, 0px))",
        zIndex: 60,
        background: "var(--chalk-board)",
        color: "var(--chalk-white)",
        borderRadius: 14,
        padding: "14px 14px 14px 16px",
        boxShadow: "0 12px 28px rgba(0,0,0,0.32)",
        display: "flex",
        gap: 12,
        alignItems: "flex-start",
        animation: "chalk-a2hs-in 320ms ease",
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 500, lineHeight: 1.3 }}>
          Get the full-screen Chalk OS experience.
        </div>
        <div style={{
          fontSize: 12, lineHeight: 1.45, marginTop: 4,
          color: "rgba(245,240,232,0.78)",
          display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap",
        }}>
          Tap
          <Share size={12} aria-label="Safari share button" style={{ verticalAlign: "middle" }} />
          <span>then</span>
          <strong style={{ fontWeight: 500, color: "var(--chalk-white)" }}>
            Add to Home Screen
          </strong>
          <span>.</span>
        </div>
      </div>
      <button
        type="button"
        data-testid="a2hs-dismiss"
        aria-label="Dismiss"
        onClick={dismiss}
        style={{
          background: "transparent",
          border: "none",
          color: "rgba(245,240,232,0.72)",
          padding: 4,
          cursor: "pointer",
          marginTop: -2,
        }}
      >
        <X size={16} aria-hidden="true" />
      </button>
      <style>{`
        @keyframes chalk-a2hs-in {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

function shouldShow() {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;
  try {
    if (localStorage.getItem(STORAGE_KEY)) return false;
  } catch {
    // localStorage blocked (private mode) — don't re-show, but also don't crash.
    return false;
  }
  const ua = navigator.userAgent || "";
  // iOS Safari detection: needs both iOS device AND no other-browser tokens.
  // Chrome iOS uses `CriOS`, Firefox iOS uses `FxiOS`, Edge iOS uses `EdgiOS`,
  // Opera iOS uses `OPiOS`. We exclude all so the user actually sees the
  // standard share menu we point at.
  const isIOS = /iPhone|iPod|iPad/i.test(ua) ||
    // iPad on iOS 13+ reports as Mac with touch — disambiguate via touch points.
    (ua.includes("Mac") && typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 1);
  if (!isIOS) return false;
  if (/CriOS|FxiOS|EdgiOS|OPiOS|YaBrowser/i.test(ua)) return false;
  // Already installed → silent.
  const inStandalone =
    (typeof window.matchMedia === "function" && window.matchMedia("(display-mode: standalone)").matches) ||
    navigator.standalone === true;
  if (inStandalone) return false;
  return true;
}
